import { useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { sessionActions, devicesActions, vehiclesActions, eventsActions, geofencesActions, maintenanceActions, groupsActions, staffGroupsActions, driversActions, notificationsActions, workActions, withScope } from '@/store';
import api from '@/lib/api';
import { ws as wsApi, vehicles as vehiclesApi, enterprise as enterpriseApi } from './trackizy.js';

const LOGOUT_CODE = 4000;
const RECONNECT_DELAY = 60000;
// A failed ticket mint (network blip, deploy) retries sooner than a dropped
// socket: nothing was ever connected, so there is no refetch to amortise.
const TICKET_RETRY_DELAY = 5000;
// Enterprise mode has no WebSocket: positions and devices are polled.
export const ENTERPRISE_POSITIONS_POLL_MS = 10_000;
export const ENTERPRISE_DEVICES_POLL_MS = 60_000;

/**
 * WebSocket controller for real-time Traccar updates.
 * Adapted from traccar-web/src/SocketController.jsx.
 *
 * Handles:
 * - Connection/reconnection with exponential backoff
 * - Device status updates
 * - Position updates
 * - Event notifications
 * - Visibility change reconnection (tab focus)
 * - Network online reconnection
 */
export default function SocketController() {
  const dispatch = useDispatch();
  const store = useStore();
  const authenticated = useSelector((state) => Boolean(state.session.user));
  // The realtime layer is scoped to the ACTIVE org, not just "am I logged in".
  // Sultans switch orgs via impersonation (which swaps the JWT + org state
  // without changing `authenticated`); keying the bootstrap effect on orgId
  // makes an org switch tear down the old socket (leaving the previous org's
  // WS room) and refetch org-scoped data for the new one. Without this, the
  // map kept showing the prior tenant's vehicles + live positions.
  const orgId = useSelector((state) => state.org.id);
  // Enterprise mode has its own read-only data layer (no WebSocket); the
  // org bootstrap below must not run there.
  const enterpriseMode = useSelector((state) => state.session.mode === 'enterprise');
  // The enterprise data layer runs once the synthetic enterprise org is set.
  const enterpriseOrg = useSelector((state) => state.org.enterprise === true);
  // Re-init on every scope switch too, not only on an org change, so a
  // connection is never left writing under a superseded generation.
  const scopeGen = useSelector((state) => state.session.scopeGen);
  const socketRef = useRef(null);
  const reconnectRef = useRef(null);
  // Monotonic connect epoch: a single-use WS ticket is fetched async before the
  // socket opens, so a newer connect() (e.g. org switch) or unmount must cancel
  // a stale in-flight one. open() no-ops unless its epoch is still current.
  const epochRef = useRef(0);
  // The org the realtime slices are currently bound to. Lets us distinguish a
  // real org SWITCH (impersonation start/exit) from the initial mount so we can
  // clear prior-tenant data on a switch without racing the very first fetch.
  const boundOrgRef = useRef(null);

  const clearReconnect = () => {
    if (reconnectRef.current) {
      clearTimeout(reconnectRef.current);
      reconnectRef.current = null;
    }
  };

  // `gen` is the scope generation this connection belongs to; every tenant
  // write it makes (messages, reconnect refetch) is stamped with it, so the
  // root reducer drops them once the user has switched scope. A timer- or
  // focus-driven reconnect reads the generation current at that moment.
  const connect = useCallback((gen = store.getState().session.scopeGen) => {
    clearReconnect();
    const myEpoch = ++epochRef.current;
    const scoped = (action) => dispatch(withScope(action, gen));

    if (socketRef.current?.readyState !== WebSocket.CLOSED) {
      socketRef.current?.close();
    }

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';

    // Opens the socket with the single-use ticket query and wires all
    // handlers. No-ops if a newer
    // connect()/unmount has advanced the epoch while the ticket was fetching.
    const open = (authQuery) => {
      if (myEpoch !== epochRef.current) return;

      const socket = new WebSocket(`${protocol}//${window.location.host}/api/socket?${authQuery}`);
      socketRef.current = socket;

      socket.onopen = () => {
        dispatch(sessionActions.updateSocket(true));
      };

      socket.onclose = async (event) => {
        // A socket from a superseded connect (scope switch, unmount, newer
        // reconnect) must never refetch or schedule a reconnect of its own.
        if (myEpoch !== epochRef.current) return;
        dispatch(sessionActions.updateSocket(false));

        if (event.code !== LOGOUT_CODE) {
          // Fetch fresh data on reconnect
          try {
            const [devices, positions, drivers] = await Promise.all([
              api.getDevices(),
              api.getPositions(),
              api.getDrivers().catch(() => []),
            ]);
            scoped(devicesActions.refresh(devices));
            scoped(sessionActions.updatePositions(positions));
            scoped(driversActions.refresh(drivers));
          } catch {
            // Will retry on next reconnect
          }

          clearReconnect();
          reconnectRef.current = setTimeout(() => connect(), RECONNECT_DELAY);
        }
      };

      socket.onmessage = (event) => {
        const data = JSON.parse(event.data);
        if (data.devices) scoped(devicesActions.update(data.devices));
        if (data.positions) scoped(sessionActions.updatePositions(data.positions));
        if (data.events) scoped(eventsActions.add(data.events));

        // Notification from fired rule — feeds the toast + bell badge
        if (data.type === 'notification' && data.notification) {
          scoped(notificationsActions.add(data.notification));
        }

        // work_* envelopes — drive React Query invalidation via useWorkRealtime
        if (typeof data.kind === 'string' && data.kind.startsWith('work_')) {
          scoped(workActions.setLastEvent({
            kind: data.kind,
            workId: data.workId,
            stopId: data.stopId,
            ts: data.ts,
          }));
        }

        // Forward presence, typing, chat/safety/broadcast/muster/command-action
        // push events to window for hooks to consume.
        // The two `command_action.*` types are also listed in
        // `COMMAND_ACTION_WS_EVENTS` (src/features/alerts/commandApprovalModel.js),
        // and that file's test pins this condition against them — a typo here
        // is otherwise invisible, since the only symptom is an approval inbox
        // that quietly never refreshes.
        if (
          data.type === 'presence' ||
          data.type === 'typing' ||
          data.type === 'chat_message' ||
          data.type === 'chat_broadcast' ||
          data.type === 'broadcast_progress' ||
          data.type === 'sos' ||
          data.type === 'sos_location' ||
          data.type === 'sos_ack' ||
          data.type === 'muster_board_changed' ||
          data.type === 'command_action.held' ||
          data.type === 'command_action.resolved'
        ) {
          window.dispatchEvent(new MessageEvent('ws-message', { data: event.data }));
        }
      };

      // Listen for outgoing messages from hooks (e.g., typing indicators)
      const handleWsSend = (e) => {
        if (socket.readyState === WebSocket.OPEN) {
          socket.send(e.detail);
        }
      };
      window.addEventListener('ws-send', handleWsSend);

      // Store cleanup ref for ws-send listener
      socket._cleanupWsSend = () => window.removeEventListener('ws-send', handleWsSend);
    };

    // Mint a single-use ticket so the JWT never rides in the WS URL (and thus
    // never lands in proxy/access logs). There is no `?token=` fallback — the
    // server refuses it (TASK-498) — so a failed mint just retries shortly.
    wsApi
      .ticket()
      .then((res) => open(`ticket=${encodeURIComponent(res?.ticket ?? '')}`))
      .catch(() => {
        if (myEpoch !== epochRef.current) return;
        dispatch(sessionActions.updateSocket(false));
        clearReconnect();
        reconnectRef.current = setTimeout(() => connect(), TICKET_RETRY_DELAY);
      });
  }, [dispatch, store]);

  // Connect when authenticated, and re-init whenever the active org changes
  // (e.g. sultan impersonation start/exit).
  useEffect(() => {
    if (!authenticated || enterpriseMode) return;
    // The scope this bootstrap belongs to: a fetch that resolves after a
    // scope switch is dropped by the root reducer (Review Focus 5).
    const gen = scopeGen;
    const scoped = (action) => dispatch(withScope(action, gen));

    // On a real org SWITCH (impersonation start/exit) clear ALL tenant-scoped
    // slices up front so the prior tenant's data is never shown under the new
    // org's scope, even if a fetch below fails. Skipped on the initial mount
    // (boundOrgRef === null) so we don't race the first fetch. This is safe
    // against the stuck-empty bug because (a) the fetches below are independent
    // — one failing no longer wipes the others (the old all-or-nothing did) —
    // and (b) connect() always runs, so the WS stream + reconnect-refetch
    // repopulate even if the initial REST fetch for one slice failed.
    if (boundOrgRef.current !== null && boundOrgRef.current !== orgId) {
      scoped(devicesActions.refresh([]));
      scoped(vehiclesActions.refresh([]));
      scoped(sessionActions.replacePositions([]));
      scoped(geofencesActions.refresh([]));
      scoped(maintenanceActions.refresh([]));
      scoped(groupsActions.refresh([]));
      scoped(staffGroupsActions.reset());
      scoped(driversActions.refresh([]));
    }
    boundOrgRef.current = orgId;

    const init = async () => {
      // Each fetch is independent: a `null` from a failed/aborted call is
      // skipped, so one failure can't reject the batch and strand every slice.
      const [devices, positions, geofences, maintenance, groups, drivers, vehicles] = await Promise.all([
        api.getDevices().catch(() => null),
        api.getPositions().catch(() => null),
        api.getGeofences().catch(() => null),
        api.getMaintenance().catch(() => null),
        api.getGroups().catch(() => null),
        api.getDrivers().catch(() => null),
        vehiclesApi.list().catch(() => null),
      ]);
      if (devices) scoped(devicesActions.refresh(devices));
      if (positions) scoped(sessionActions.updatePositions(positions));
      if (geofences) scoped(geofencesActions.refresh(geofences));
      if (maintenance) scoped(maintenanceActions.refresh(maintenance));
      if (groups) scoped(groupsActions.refresh(groups));
      if (drivers) scoped(driversActions.refresh(drivers));
      if (vehicles) scoped(vehiclesActions.refresh(vehicles));
      connect(gen); // always connect, even if a bootstrap fetch failed
    };

    init();

    return () => {
      clearReconnect();
      // Cancel any in-flight ticket fetch so its socket never opens post-unmount.
      epochRef.current += 1;
      socketRef.current?.close(LOGOUT_CODE);
    };
  }, [authenticated, enterpriseMode, orgId, scopeGen, connect, dispatch]);

  // Enterprise mode: a read-only, polled data layer over /api/enterprise/*.
  // No WebSocket (there is no enterprise room), no org endpoints; geofences,
  // maintenance and drivers have no enterprise equivalent and stay empty.
  useEffect(() => {
    if (!authenticated || !enterpriseMode || !enterpriseOrg) return;
    const gen = scopeGen;
    const scoped = (action) => dispatch(withScope(action, gen));
    let cancelled = false;

    scoped(geofencesActions.refresh([]));
    scoped(maintenanceActions.refresh([]));
    scoped(driversActions.refresh([]));

    // One positions request at a time: a slow response skips the next tick
    // instead of stacking requests (and out-of-order replacements).
    let positionsInFlight = false;
    const loadPositions = async () => {
      if (positionsInFlight) return;
      positionsInFlight = true;
      try {
        const positions = await enterpriseApi.positions().catch(() => null);
        if (!cancelled && Array.isArray(positions)) scoped(sessionActions.replacePositions(positions));
      } finally {
        positionsInFlight = false;
      }
    };
    const loadDevices = async () => {
      const devices = await enterpriseApi.devices().catch(() => null);
      if (!cancelled && Array.isArray(devices)) scoped(devicesActions.refresh(devices));
    };
    const init = async () => {
      const [devices, positions, groups, vehicles] = await Promise.all([
        enterpriseApi.devices().catch(() => null),
        enterpriseApi.positions().catch(() => null),
        enterpriseApi.groups().catch(() => null),
        enterpriseApi.vehicles().catch(() => null),
      ]);
      if (cancelled) return;
      if (Array.isArray(devices)) scoped(devicesActions.refresh(devices));
      if (Array.isArray(positions)) scoped(sessionActions.replacePositions(positions));
      if (Array.isArray(groups)) scoped(groupsActions.refresh(groups));
      if (Array.isArray(vehicles)) scoped(vehiclesActions.refresh(vehicles));
    };

    init();
    const positionsTimer = setInterval(loadPositions, ENTERPRISE_POSITIONS_POLL_MS);
    const devicesTimer = setInterval(loadDevices, ENTERPRISE_DEVICES_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(positionsTimer);
      clearInterval(devicesTimer);
    };
  }, [authenticated, enterpriseMode, enterpriseOrg, scopeGen, dispatch]);

  // Reconnect on tab focus / network online
  useEffect(() => {
    if (!authenticated || enterpriseMode) return;

    const reconnectIfNeeded = () => {
      const socket = socketRef.current;
      if (!socket || socket.readyState === WebSocket.CLOSED) {
        connect();
      }
    };

    const onVisibility = () => { if (!document.hidden) reconnectIfNeeded(); };

    window.addEventListener('online', reconnectIfNeeded);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.removeEventListener('online', reconnectIfNeeded);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [authenticated, enterpriseMode, connect]);

  return null; // Headless controller
}
